import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UgcAdCharacter, UgcAdCharacterFeatures } from '../../services/api/ugcAdService';
import { TaskyButton, TaskyCard, TaskyAlert, TaskyInput, TaskyEmptyState } from '../tasky';
import GenerationProgress from './GenerationProgress';
import { cn } from '../../lib/utils';
import { Loader2, Plus, RefreshCw, Sparkles, UserRound, X } from 'lucide-react';

/**
 * ステップ③: キャラクター（登場人物の固定）。
 *
 * ## なぜこの画面が要るのか
 * 何もしないとシーンごとに別人の顔になる。ここで人物を1枚描き、その絵を読み取って
 * 「同じ人物を描き続けるためのテキスト」を確定しておく。以降の絵コンテ・クリップ生成は
 * **この画像（出演者リファレンス）とテキストの両方**で人物を固定する。
 *
 * 画像を参照として渡せるかは動画モデル次第で、Seedance は写真のような人物画像を必ず拒否する
 * （その場合はテキストだけで固定する）。クリップの既定モデルは受け付ける側の MiniMax H3。
 *
 * 画面に出すのは**画像と特徴だけ**。生成に埋め込まれる完成形のロック文は長文で編集対象に
 * ならないため表示しない（backend が内部で保持する）。
 */

interface CharacterStepProps {
  character: UgcAdCharacter;
  /** 特徴を指定して生成する。未指定なら商品・出演者設定から初回の案を起こす */
  onGenerate: (features?: UgcAdCharacterFeatures) => Promise<void>;
  onProceed: () => void;
}

type FeatureListKey = 'appearance' | 'outfit' | 'voice';

const LIST_LABELS: Record<FeatureListKey, { title: string; hint: string }> = {
  appearance: { title: '見た目', hint: '顔立ち・髪・肌など' },
  outfit: { title: '衣装', hint: '全カットで同じ服を着ます' },
  voice: { title: '声', hint: '動画の発話音声をブレさせないための指定です' },
};

/**
 * 同期判定に使う値。ポーリングのたびに新しいオブジェクトが来るので、参照ではなく
 * **値**で比較しないと編集中の入力が毎回サーバーの値へ巻き戻る（UGC-03）。
 */
const serializeFeatures = (features: UgcAdCharacterFeatures): string =>
  JSON.stringify([features.appearance, features.outfit, features.voice, features.persona ?? '']);

/** 「日本人男性・30代半ば」のような、いま適用されている条件の1行表示 */
export const describeAppliedFeatures = (features: UgcAdCharacterFeatures | null): string | null => {
  if (!features) return null;

  const head = features.appearance.filter((v) => v.trim() !== '').slice(0, 2);
  if (head.length > 0) return head.join('・');

  return features.persona?.trim() || null;
};

/** キャラクター生成の処理段階（クライアント側の演出。backendは細かい進捗を返さない） */
const CHARACTER_STAGES = [
  '人物像を設計しています',
  '画像を生成しています',
  '生成した絵から特徴を読み取っています',
];

const CharacterStep: React.FC<CharacterStepProps> = ({ character, onGenerate, onProceed }) => {
  const isGenerating = character.status === 'pending' || character.status === 'processing';

  const [draft, setDraft] = useState<UgcAdCharacterFeatures>(character.features);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 編集中はサーバー値で上書きしない。生成完了までポーリングが走り続けるため、
  // このガードが無いと入力途中の文字が数秒ごとに消える
  const [isDirty, setIsDirty] = useState(false);
  const syncedRef = useRef(serializeFeatures(character.features));

  // サーバー側で特徴が「値として」変わったときだけ同期する（生成完了・再訪時）
  useEffect(() => {
    const next = serializeFeatures(character.features);
    if (next === syncedRef.current) return;

    syncedRef.current = next;
    if (isDirty) return;

    setDraft(character.features);
  }, [character.features, isDirty]);

  /** 編集は必ずここを通す。dirty を立ててポーリングによる巻き戻りを止める */
  const editDraft = useCallback(
    (updater: (prev: UgcAdCharacterFeatures) => UgcAdCharacterFeatures) => {
      setIsDirty(true);
      setDraft(updater);
    },
    []
  );

  const hasFeatures = useMemo(
    () => draft.appearance.length > 0 || draft.outfit.length > 0 || draft.voice.length > 0,
    [draft]
  );

  const updateItem = (key: FeatureListKey, index: number, value: string) => {
    editDraft((prev) => ({
      ...prev,
      [key]: prev[key].map((item, i) => (i === index ? value : item)),
    }));
  };

  const removeItem = (key: FeatureListKey, index: number) => {
    editDraft((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));
  };

  const addItem = (key: FeatureListKey) => {
    editDraft((prev) => ({ ...prev, [key]: [...prev[key], ''] }));
  };

  const submit = async (features?: UgcAdCharacterFeatures) => {
    setIsSubmitting(true);
    try {
      await onGenerate(features);
      // 送った内容がサーバー側の正本になったので、以降はサーバー値を受け入れてよい。
      // **失敗時は dirty のまま**にして利用者の入力を保持する
      setIsDirty(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerate = () => {
    // 空欄は送らない（保存側でも落とすが、往復させない）
    submit({
      appearance: draft.appearance.map((v) => v.trim()).filter(Boolean),
      outfit: draft.outfit.map((v) => v.trim()).filter(Boolean),
      voice: draft.voice.map((v) => v.trim()).filter(Boolean),
      persona: draft.persona?.trim() || null,
    });
  };

  const busy = isGenerating || isSubmitting;
  // 「何で作り直しているのか」を生成中に見せる。requested が無い初回は draft を使う
  const appliedCondition = useMemo(
    () => describeAppliedFeatures(character.requested_features) ?? describeAppliedFeatures(draft),
    [character.requested_features, draft]
  );

  /* ------------------------------------------------------------------ */
  /* 初回（まだ一度も作っていない）                                        */
  /* ------------------------------------------------------------------ */

  if (!character.image_url && !isGenerating) {
    return (
      <div className="max-w-3xl mx-auto">
        {character.status === 'failed' && character.error_message && (
          <TaskyAlert tone="error" className="mb-4">
            {character.error_message}
          </TaskyAlert>
        )}

        <TaskyEmptyState
          icon={<UserRound />}
          title="動画に出演する人を決めます"
          action={
            <TaskyButton onClick={() => submit()} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              キャラクターを作る
            </TaskyButton>
          }
        />
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* 生成済み / 生成中                                                    */
  /* ------------------------------------------------------------------ */

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-5">
        <h2 className="text-lg font-semibold text-dark">キャラクター</h2>
      </div>

      {character.status === 'failed' && character.error_message && (
        <TaskyAlert tone="error" className="mb-4">
          {character.error_message}
          <span className="block text-xs mt-1 opacity-80">
            入力した特徴はそのまま残っています。内容を見直してもう一度お試しください。
          </span>
        </TaskyAlert>
      )}

      {busy && appliedCondition && (
        <p className="mb-2 text-center text-xs text-gray-600">「{appliedCondition}」で再生成しています</p>
      )}

      {/* 何を処理していて、どれくらい待つかを出す（UGC-02） */}
      <GenerationProgress
        active={busy}
        stages={CHARACTER_STAGES}
        estimate="通常30秒ほどかかります"
        className="mb-4"
      />

      <div className="grid gap-5 sm:grid-cols-[minmax(0,260px)_1fr] items-start">
        {/* 左: キャラクター画像 */}
        <div className="bg-[#F4F3EF] rounded-2xl p-4">
          <div
            className="relative rounded-xl overflow-hidden bg-gray-100 mx-auto"
            style={{ aspectRatio: '2 / 3', maxWidth: 220 }}
          >
            {character.image_url ? (
              <img src={character.image_url} alt="キャラクター" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <UserRound className="w-10 h-10 text-gray-300" />
              </div>
            )}

            {isGenerating && (
              <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center gap-2 px-3 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                {/* 目安時間は GenerationProgress 側に出すので、ここは状態だけ */}
                <span className="text-[11px] text-gray-600">
                  {appliedCondition ? `${appliedCondition}で生成中…` : '生成中…'}
                </span>
              </div>
            )}
          </div>

          {draft.persona && (
            <p className="text-xs text-gray-600 text-center mt-3 leading-relaxed">{draft.persona}</p>
          )}
        </div>

        {/* 右: 特徴（編集可能） */}
        <div className="space-y-4">
          {(Object.keys(LIST_LABELS) as FeatureListKey[]).map((key) => (
            <TaskyCard key={key} variant="warm">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-dark">{LIST_LABELS[key].title}</h3>
                <span className="text-[11px] text-gray-400">{LIST_LABELS[key].hint}</span>
              </div>

              <div className="space-y-1.5">
                {draft[key].map((item, index) => (
                  <div key={`${key}-${index}`} className="flex items-center gap-1.5">
                    <TaskyInput
                      value={item}
                      maxLength={60}
                      disabled={busy}
                      onChange={(e) => updateItem(key, index, e.target.value)}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeItem(key, index)}
                      disabled={busy}
                      className="p-1.5 text-gray-400 hover:text-danger disabled:opacity-40"
                      aria-label={`${LIST_LABELS[key].title}の項目を削除`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => addItem(key)}
                  disabled={busy}
                  className="flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-40"
                >
                  <Plus className="w-3 h-3" />
                  項目を追加
                </button>
              </div>
            </TaskyCard>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <TaskyButton variant="outline" onClick={handleRegenerate} disabled={busy || !hasFeatures}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              特徴を変えて作り直す
            </TaskyButton>

            <TaskyButton onClick={onProceed} disabled={busy || !character.locked}>
              このキャラクターで進む
            </TaskyButton>
          </div>

          <p className={cn('text-[11px]', character.locked ? 'text-gray-400' : 'text-gray-500')}>
            {character.locked
              ? 'このあとの絵コンテ・動画はこの特徴をもとに作られます。'
              : '作り直すと画像の生成にクレジットを消費します。'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CharacterStep;
